import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Save, Copy } from "lucide-react";
import api, { errMsg } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { inr, monthLabel } from "../lib/format";
import { PageHeader, Loading, SectionTitle } from "../components/kit";
import { Button } from "../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";

const BASE = [["sales", "Sales ₹"], ["gms", "GMS ₹"], ["walkins", "Walk-ins"], ["bills", "Bills"], ["units", "Units"],
  ["conversion", "Conv %"], ["atv", "ATV ₹"], ["asp", "ASP ₹"], ["upt", "UPT"]];

function Row({ label, sub, metrics, cats, onSave, canEdit, testid }) {
  const [m, setM] = useState(metrics || { cats: {} });
  useEffect(() => setM(metrics || { cats: {} }), [metrics]);
  const cell = (val, on, tid) => (
    <input type="number" inputMode="decimal" value={val ?? ""} onChange={(e) => on(e.target.value === "" ? 0 : Number(e.target.value))} disabled={!canEdit}
      className="w-24 h-10 rounded-lg border border-slate-200 px-2 num text-right text-sm focus:outline-none focus:border-[#011E38] disabled:bg-slate-50" data-testid={tid} />
  );
  return (
    <tr className="border-b border-slate-50" data-testid={testid}>
      <td className="px-4 py-3 sticky left-0 bg-white"><div className="font-semibold text-[#011E38]">{label}</div><div className="text-xs text-slate-500">{sub}</div></td>
      {BASE.map(([k]) => <td key={k} className="px-1.5">{cell(m[k], (v) => setM({ ...m, [k]: v }), `${testid}-${k}`)}</td>)}
      {cats.map((c) => <td key={c.key} className="px-1.5">{cell(m.cats?.[c.key], (v) => setM({ ...m, cats: { ...m.cats, [c.key]: v } }), `${testid}-cat-${c.key}`)}</td>)}
      <td className="px-3">{canEdit && <Button size="sm" onClick={() => onSave(m)} className="bg-[#011E38]" data-testid={`${testid}-save`}><Save className="w-4 h-4" /></Button>}</td>
    </tr>
  );
}

export default function Targets() {
  const { stores, storeId, month } = useAuth();
  const [sid, setSid] = useState(storeId !== "all" ? storeId : stores[0]?.id);
  const [d, setD] = useState(null);
  const [cats, setCats] = useState([]);

  const load = useCallback(() => {
    if (!sid) return;
    Promise.all([api.get("/targets", { params: { store_id: sid, month } }), api.get("/categories")]).then(([t, c]) => { setD(t.data); setCats(c.data); });
  }, [sid, month]);
  useEffect(() => { load(); }, [load]);

  const save = async (staff_id, metrics) => {
    try { await api.put("/targets", { store_id: sid, month, staff_id, metrics }); toast.success("Target saved"); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };

  const { user } = useAuth();
  const [y, m] = month.split("-").map(Number);
  const prev = m === 1 ? `${y - 1}-12` : `${y}-${String(m - 1).padStart(2, "0")}`;
  const copy = async () => {
    const hasAny = d.store || d.staff.some((s) => s.metrics);
    const overwrite = hasAny && window.confirm(`${monthLabel(month)} already has targets. Overwrite them with ${monthLabel(prev)}? Cancel keeps existing ones and only fills missing.`);
    try {
      const r = await api.post("/targets/copy", { store_id: sid, from_month: prev, to_month: month, overwrite });
      toast.success(`Copied ${r.data.copied} targets from ${monthLabel(prev)}${r.data.skipped ? ` · ${r.data.skipped} kept` : ""}`);
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const staffSum = d ? d.staff.reduce((a, s) => a + (s.metrics?.sales || 0), 0) : 0;

  return (
    <div className="space-y-8">
      <PageHeader eyebrow={monthLabel(month)} title="Targets" testid="targets-title"
        right={<div className="flex flex-wrap gap-2">
          {user.role === "admin" && sid && d && (
            <Button variant="outline" onClick={copy} className="h-11 rounded-full px-5" data-testid="copy-last-month-button">
              <Copy className="w-4 h-4 mr-2" /> Copy from {monthLabel(prev)}
            </Button>
          )}
          {stores.length > 1 && (
            <Select value={sid} onValueChange={setSid}>
              <SelectTrigger className="w-60 h-11" data-testid="targets-store-select"><SelectValue /></SelectTrigger>
              <SelectContent>{stores.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
            </Select>
          )}
        </div>} />
      {!sid ? <p className="text-slate-500">Create a store first.</p> : !d ? <Loading /> : (
        <>
          <div className="grid sm:grid-cols-3 gap-5">
            <div className="bs-card p-6"><div className="bs-eyebrow">Store sales target</div><div className="num text-4xl font-bold text-[#011E38] mt-2" data-testid="targets-store-sales">{inr(d.store?.sales)}</div></div>
            <div className="bs-card p-6"><div className="bs-eyebrow">Sum of staff targets</div><div className="num text-4xl font-bold text-[#011E38] mt-2" data-testid="targets-staff-sum">{inr(staffSum)}</div></div>
            <div className="bs-card p-6"><div className="bs-eyebrow">Store GMS target</div><div className="num text-4xl font-bold text-[#011E38] mt-2">{inr(d.store?.gms)}</div></div>
          </div>
          {!d.can_edit && <div className="rounded-xl bg-sky-50 text-sky-800 px-4 py-3 text-sm" data-testid="targets-readonly-note">Targets are view-only for managers. Ask Admin to change them, or Admin can enable manager editing in Settings.</div>}
          <section>
            <SectionTitle eyebrow="Store · Staff · KPI · Category" title="Monthly targets" />
            <div className="bs-card overflow-x-auto">
              <table className="text-left" data-testid="targets-table">
                <thead className="text-xs uppercase tracking-wider text-slate-500 border-b border-slate-100">
                  <tr><th className="px-4 py-4 sticky left-0 bg-white">Name</th>{BASE.map(([k, l]) => <th key={k} className="px-1.5 py-4 text-right">{l}</th>)}{cats.map((c) => <th key={c.key} className="px-1.5 py-4 text-right">{c.name} ₹</th>)}<th /></tr>
                </thead>
                <tbody>
                  <Row label="Store total" sub="Store-level target" metrics={d.store} cats={cats} canEdit={d.can_edit} onSave={(m) => save(null, m)} testid="target-row-store" />
                  {d.staff.map((s) => (
                    <Row key={s.id} label={s.name} sub={`${s.employee_id}${s.store_id !== sid ? " · transferred" : ""}`} metrics={s.metrics} cats={cats} canEdit={d.can_edit} onSave={(m) => save(s.id, m)} testid={`target-row-${s.employee_id}`} />
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        </>
      )}
    </div>
  );
}
